import React, { Component } from "react"
import PropTypes from "prop-types"

import Flatpickr from "react-flatpickr"
import English from "flatpickr/dist/l10n"
import { Danish } from "flatpickr/dist/l10n/da.js"
import { German } from "flatpickr/dist/l10n/de.js"
import { Italian } from "flatpickr/dist/l10n/it.js"
import { Swedish } from "flatpickr/dist/l10n/sv.js"
import Dropdown from "react-dropdown"
import moment from "moment"
import CountryContext from "component_library/contexts/countryContext"

class BookingDateTimePicker extends Component {
  static propTypes = {
    translations: PropTypes.object,
    datePickerProps: PropTypes.object.isRequired,
    onDateTimeChange: PropTypes.func,
    styles: PropTypes.object,
    country: PropTypes.string,
  }

  _findExcludedDays = () => {
    const excludedDays = []

    // Don't allow to book if dealership is closed
    this.props.datePickerProps.datePickerDayOfWeekException.forEach((day) => {
      let date = moment(
        this.props.datePickerProps.datePickerLimitLower
      ).isoWeekday(parseInt(day))
      while (date < new Date(this.props.datePickerProps.datePickerLimitUpper)) {
        excludedDays.push(new Date(date.toString()))
        date = date.add(7, "d")
      }
    })
    return excludedDays
  }

  _findClosestAvailableDay = (date) => {
    if (this._findExcludedDays().length > 0) {
      const excludedTimes = this._findExcludedDays().map((time) =>
        time.getTime()
      )
      const currentDate = new Date(date)
      const currentTime = currentDate.setHours(0, 0, 0, 0)

      if (excludedTimes.includes(currentTime)) {
        const dateArray = []
        let currentDate = moment(
          this.props.datePickerProps.datePickerLimitLower
        )
        const lastDate = moment(this.props.datePickerProps.datePickerLimitUpper)
        while (currentDate <= lastDate) {
          dateArray.push(new Date(currentDate).getTime())
          currentDate = currentDate.add(1, "days")
        }
        const firstDate = dateArray.filter(
          (date) => !excludedTimes.includes(date)
        )[0]
        return new Date(firstDate)
      } else {
        return date
      }
    } else {
      return date
    }
  }

  _findHourOptions = (dayNum) => {
    if (
      this.props.datePickerProps.customSchedule &&
      this.props.datePickerProps.customSchedule[dayNum] &&
      this.props.datePickerProps.customSchedule[dayNum].length > 0
    ) {
      return this.props.datePickerProps.customSchedule[dayNum]
    } else {
      return this.props.datePickerProps.customSchedule["default"]
    }
  }

  _findMinutesOptions = (dayNum, hour) => {
    let timeMinutesOptions = ["00", "15", "30", "45"]

    const currentDaySchedule =
      this.props.datePickerProps.schedule[dayNum] ||
      this.props.datePickerProps.schedule["default"]

    if (currentDaySchedule !== "closed") {
      const openingTimeHour = currentDaySchedule[0].split(":")[0]
      const openingTimeMinutes = currentDaySchedule[0].split(":")[1]
      const closingTimeHour = currentDaySchedule[1].split(":")[0]
      const closingTimeMinutes = currentDaySchedule[1].split(":")[1]

      if (hour === openingTimeHour) {
        return timeMinutesOptions.filter(
          (option) => option >= openingTimeMinutes
        )
      } else if (hour === closingTimeHour) {
        return timeMinutesOptions.filter(
          (option) => option <= closingTimeMinutes
        )
      } else {
        return timeMinutesOptions
      }
    } else {
      return []
    }
  }

  state = {
    date: this._findClosestAvailableDay(
      new Date(this.props.datePickerProps.datePickerLimitLower)
    ),
    dayNum: moment(
      this._findClosestAvailableDay(
        new Date(this.props.datePickerProps.datePickerLimitLower)
      )
    ).isoWeekday(),
    timeHour: this._findHourOptions(
      moment(
        this._findClosestAvailableDay(
          new Date(this.props.datePickerProps.datePickerLimitLower)
        )
      ).isoWeekday()
    )[0],
    timeMinutes: this._findMinutesOptions(
      moment(
        this._findClosestAvailableDay(
          new Date(this.props.datePickerProps.datePickerLimitLower)
        )
      ).isoWeekday(),
      this._findHourOptions(
        moment(
          new Date(this.props.datePickerProps.datePickerLimitLower)
        ).isoWeekday()
      )[0]
    )[0],
  }

  componentDidMount() {
    if (typeof this.props.onDateTimeChange === "function") {
      const newDateTime = new Date(this.state.date.getTime())
      newDateTime.setHours(this.state.timeHour, this.state.timeMinutes)
      this.props.onDateTimeChange(newDateTime)
    }
  }

  _handleChangeHour = (option) => {
    this.setState(
      (prevState) => ({
        timeHour: option.value,
        timeMinutes: this._findMinutesOptions(
          prevState.dayNum,
          option.value
        ).includes(prevState.timeMinutes)
          ? prevState.timeMinutes
          : this._findMinutesOptions(prevState.dayNum, option.value)[0],
      }),
      () => {
        if (typeof this.props.onDateTimeChange === "function") {
          const newDate = new Date(this.state.date.getTime())
          newDate.setHours(option.value, this.state.timeMinutes)
          this.props.onDateTimeChange(newDate)
        }
      }
    )
  }

  _handleChangeMinutes = (option) => {
    this.setState({ timeMinutes: option.value })

    if (typeof this.props.onDateTimeChange === "function") {
      const newDate = new Date(this.state.date.getTime())
      newDate.setHours(this.state.timeHour, option.value)
      this.props.onDateTimeChange(newDate)
    }
  }

  _handleDateChange = (selectedDates) => {
    const newDate = selectedDates[0]
    this.setState(
      (prevState) => ({
        date: newDate,
        dayNum: moment(newDate).isoWeekday(),
        timeHour: this._findHourOptions(moment(newDate).isoWeekday()).includes(
          prevState.timeHour
        )
          ? prevState.timeHour
          : this._findHourOptions(moment(newDate).isoWeekday())[0],
      }),
      () => {
        if (typeof this.props.onDateTimeChange === "function") {
          const newDateTime = new Date(newDate.getTime())
          newDateTime.setHours(this.state.timeHour, this.state.timeMinutes)
          this.props.onDateTimeChange(newDateTime)
        }
      }
    )
  }

  _findOpeningTimes = (dayNum) => {
    if (
      this.props.datePickerProps.schedule &&
      this.props.datePickerProps.schedule[dayNum]
    ) {
      return `${this.props.datePickerProps.schedule[dayNum][0]}-${this.props.datePickerProps.schedule[dayNum][1]}`
    } else {
      return `${this.props.datePickerProps.schedule["default"][0]}-${this.props.datePickerProps.schedule["default"][1]}`
    }
  }

  /**
   * Returns the flatpickr locale for the country
   *
   * @param {string} country CountryManager country code
   * @returns {object}
   */
  localeForCountry = (country) => {
    return (
      {
        da: Danish,
        de: German,
        at: German,
        ch: German,
        it: Italian,
        se: Swedish,
      }[country] || English
    )
  }

  render() {
    const { styles } = this.props

    // Make sure to show right opening times for the chosen day
    let hourOptions = this._findHourOptions(this.state.dayNum)
    let timeMinutesOptions = this._findMinutesOptions(
      this.state.dayNum,
      this.state.timeHour
    )

    const openingTime = this._findOpeningTimes(this.state.dayNum)

    const datePickerOptions = {
      allowInput: false,
      minDate: new Date(this.props.datePickerProps.datePickerLimitLower),
      maxDate: new Date(this.props.datePickerProps.datePickerLimitUpper),
      disable: this._findExcludedDays(),
      dateFormat: "d/m/Y",
    }

    return (
      <CountryContext.Consumer>
        {(country) => (
          <>
            <div className={`form-row ${styles.datePicker}`}>
              <label>
                {this.props.translations.labels.date}
                <Flatpickr
                  options={{
                    ...datePickerOptions,
                    locale: this.localeForCountry(
                      country || this.props.country
                    ),
                  }}
                  value={this.state.date}
                  name="date"
                  className="c-date-picker"
                  onChange={(selectedDates) =>
                    this._handleDateChange(selectedDates)
                  }
                />
              </label>
            </div>
            <div className={styles.timePicker}>
              <label style={{ marginBottom: "5px" }}>
                {this.props.translations.labels.time} {openingTime}
                {this.props.translations.labels.timeHour}
              </label>
              <Dropdown
                placeholder="Hour"
                options={hourOptions}
                value={this.state.timeHour}
                className="half-width"
                onChange={(option) => this._handleChangeHour(option)}
              />
              <Dropdown
                placeholder="Minutes"
                options={timeMinutesOptions}
                value={this.state.timeMinutes}
                className="half-width"
                onChange={(option) => this._handleChangeMinutes(option)}
              />
            </div>
          </>
        )}
      </CountryContext.Consumer>
    )
  }
}

export default BookingDateTimePicker
